import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DifficultyCategory, StrengthOfSchedulePoint } from './types';
import { CHROME } from './dataViz';
import DifficultyBadge, { DIFFICULTY_META } from './DifficultyBadge';

interface Props {
  data: StrengthOfSchedulePoint[];
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const shortOpp = (name: string): string => (name.length > 14 ? name.slice(0, 12) + '…' : name);

interface TooltipPayload {
  payload?: {
    opponent: string;
    date: string;
    team_avg_rating: number;
    opponent_avg_rating: number;
    rating_diff: number;
    category: DifficultyCategory;
    match_won: boolean | null;
  };
}

const CustomTooltip: React.FC<{ active?: boolean; payload?: TooltipPayload[] }> = ({
  active,
  payload,
}) => {
  if (!active || !payload || payload.length === 0 || !payload[0].payload) return null;
  const p = payload[0].payload;
  const outcome =
    p.match_won === true ? 'Won' : p.match_won === false ? 'Lost' : 'Result pending';
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-gray-900 dark:text-gray-100">{p.opponent}</div>
      <div className="text-gray-500 dark:text-gray-400">{formatDate(p.date)}</div>
      <div className="mt-1 text-gray-700 dark:text-gray-300">
        Your team avg: {p.team_avg_rating.toFixed(2)}
      </div>
      <div className="text-gray-700 dark:text-gray-300">
        Opponent avg: {p.opponent_avg_rating.toFixed(2)}
      </div>
      <div className="mt-1">
        <DifficultyBadge category={p.category} diff={p.rating_diff} variant="dot" />
      </div>
      <div className="text-gray-500 dark:text-gray-400">{outcome}</div>
    </div>
  );
};

const StrengthOfScheduleChart: React.FC<Props> = ({ data }) => {
  if (data.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No opponent rating data available yet.
      </p>
    );
  }

  const chartData = data.map((d) => ({
    label: shortOpp(d.opponent_name),
    opponent: d.opponent_name,
    date: d.date,
    team_avg_rating: d.team_avg_rating,
    opponent_avg_rating: d.opponent_avg_rating,
    rating_diff: d.rating_diff,
    category: d.category,
    match_won: d.match_won,
  }));

  return (
    <div data-testid="strength-of-schedule-chart">
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHROME.grid} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{
                value: 'Opponent − Team',
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 11, fill: CHROME.axis },
              }}
            />
            <ReferenceLine y={0} stroke={CHROME.reference} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: CHROME.cursor }} />
            <Bar dataKey="rating_diff" name="Difficulty (opp − team)">
              {chartData.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={DIFFICULTY_META[entry.category].color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap gap-3">
        {(Object.keys(DIFFICULTY_META) as DifficultyCategory[]).map((c) => (
          <DifficultyBadge key={c} category={c} variant="dot" />
        ))}
      </div>
    </div>
  );
};

export default StrengthOfScheduleChart;
